import { useState, useEffect, useCallback, useMemo } from 'react'

import { WorkorderResponse } from '../store'
import { axiosInstance } from '../helpers'

// Data structure that matches your backend response
export interface ScheduleUnitData {
    id: number
    unit_name: string
    unit_location: string
    unit_config_name: string
    occupiable_areas_count: number
    // Support both letter-based (bed_A_status_tag) and number-based (bed_1_status_tag) naming
    [key: string]: any // Allow dynamic bedroom status tags
    workorders?: WorkorderResponse[]
}

export interface UseSchedulerSpreadsheetOptions {
    scheduleId?: number
    location?: string
    unitType?: string
    status?: string
    autoFetch?: boolean
}

export interface UseSchedulerSpreadsheetReturn {
    // Data
    units: ScheduleUnitData[]
    isLoading: boolean
    error: string | null

    // Actions
    fetchUnits: (scheduleId?: number) => Promise<void>
    refreshUnits: () => Promise<void>
    setUnits: (units: ScheduleUnitData[]) => void

    // Computed values
    totalUnits: number
    maxBedrooms: number
    bedroomNamingConvention: 'letters' | 'numbers'
    unitTypes: string[]
    locations: string[]

    // Filtering
    filteredUnits: ScheduleUnitData[]
    setFilters: (filters: Partial<UseSchedulerSpreadsheetOptions>) => void
    clearFilters: () => void
}

export const useSchedulerSpreadsheet = (
    options: UseSchedulerSpreadsheetOptions = {},
): UseSchedulerSpreadsheetReturn => {
    const { scheduleId, location, unitType, status, autoFetch = true } = options

    // State
    const [units, setUnits] = useState<ScheduleUnitData[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [filters, setFiltersState] = useState({
        location: location || '',
        unitType: unitType || '',
        status: status || '',
    })

    // Fetch units from API
    const fetchUnits = useCallback(
        async (scheduleIdParam?: number) => {
            const targetScheduleId = scheduleIdParam || scheduleId
            if (!targetScheduleId) {
                setError('Schedule ID is required')
                return
            }

            setIsLoading(true)
            setError(null)

            try {
                const response = await axiosInstance.get(
                    'workorder/schedule-spreadsheet/',
                    {
                        params: {
                            schedule_id: targetScheduleId,
                        },
                    },
                )

                // Assuming the response structure has the units data
                // Adjust this based on your actual API response structure
                const responseData = response.data
                const unitsData =
                    responseData.units ||
                    responseData.data ||
                    responseData ||
                    []

                setUnits(unitsData)
            } catch (err: any) {
                console.error('Error fetching schedule units:', err)
                setError(
                    err.response?.data?.message ||
                        err.message ||
                        'Failed to fetch units',
                )
            } finally {
                setIsLoading(false)
            }
        },
        [scheduleId],
    )

    // Refresh units
    const refreshUnits = useCallback(async () => {
        await fetchUnits()
    }, [fetchUnits])

    // Set filters
    const setFilters = useCallback(
        (newFilters: Partial<UseSchedulerSpreadsheetOptions>) => {
            setFiltersState((prev) => ({ ...prev, ...newFilters }))
        },
        [],
    )

    // Clear filters
    const clearFilters = useCallback(() => {
        setFiltersState({
            location: '',
            unitType: '',
            status: '',
        })
    }, [])

    // Computed values
    const totalUnits = useMemo(() => units.length, [units])

    // Detect bedroom naming convention (letters vs numbers)
    const bedroomNamingConvention = useMemo(() => {
        if (units.length === 0) return 'letters'

        // Check if any unit has letter-based bedroom status tags
        const hasLetterBeds = units.some((unit) =>
            Object.keys(unit).some((key) =>
                key.match(/^bed_[A-Z]_status_tag$/),
            ),
        )

        // Check if any unit has number-based bedroom status tags
        const hasNumberBeds = units.some((unit) =>
            Object.keys(unit).some((key) => key.match(/^bed_\d+_status_tag$/)),
        )

        // Prefer letters if both exist, otherwise use what's available
        if (hasLetterBeds) return 'letters'
        if (hasNumberBeds) return 'numbers'
        return 'letters' // default fallback
    }, [units])

    const maxBedrooms = useMemo(() => {
        if (units.length === 0) return 0
        return Math.max(...units.map((unit) => unit.occupiable_areas_count))
    }, [units])

    const unitTypes = useMemo(() => {
        const types = new Set(units.map((unit) => unit.unit_config_name))
        return Array.from(types).sort()
    }, [units])

    const locations = useMemo(() => {
        const locs = new Set(units.map((unit) => unit.unit_location))
        return Array.from(locs).sort()
    }, [units])

    // Filtered units
    const filteredUnits = useMemo(() => {
        return units.filter((unit) => {
            if (
                filters.location &&
                !unit.unit_location.includes(filters.location)
            ) {
                return false
            }
            if (
                filters.unitType &&
                unit.unit_config_name !== filters.unitType
            ) {
                return false
            }
            if (filters.status) {
                // Check if any bedroom has the specified status
                const hasStatus = Object.entries(unit)
                    .filter(
                        ([key]) =>
                            key.startsWith('bed_') &&
                            key.endsWith('_status_tag'),
                    )
                    .some(([_, value]) => value === filters.status)
                if (!hasStatus) return false
            }
            return true
        })
    }, [units, filters])

    // Auto-fetch on mount if enabled
    useEffect(() => {
        if (autoFetch && scheduleId) {
            fetchUnits()
        }
    }, [autoFetch, scheduleId, fetchUnits])

    return {
        // Data
        units,
        isLoading,
        error,

        // Actions
        fetchUnits,
        refreshUnits,
        setUnits,

        // Computed values
        totalUnits,
        maxBedrooms,
        bedroomNamingConvention,
        unitTypes,
        locations,

        // Filtering
        filteredUnits,
        setFilters,
        clearFilters,
    }
}
